import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ClassifiedAd } from '../models/classified-ad.model';
import { MockDataService } from './mock-data.service';

@Injectable({
  providedIn: 'root',
})
export class ClassifiedAdService {
  private mockDataService = inject(MockDataService);

  constructor() {}

  getAll(): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    return of([...data.classifiedAds]);
  }

  getById(id: number): Observable<ClassifiedAd | undefined> {
    const data = this.mockDataService.createDb();
    const ad = data.classifiedAds.find((a: ClassifiedAd) => a.id === id);
    return of(ad);
  }

  create(ad: Omit<ClassifiedAd, 'id' | 'createdDate' | 'updatedDate'>): Observable<ClassifiedAd> {
    const data = this.mockDataService.createDb();
    const newAd: ClassifiedAd = {
      ...ad,
      id: this.getNextId(data.classifiedAds),
      createdDate: new Date(),
      updatedDate: new Date(),
    };

    return of(newAd);
  }

  update(id: number, ad: Partial<ClassifiedAd>): Observable<ClassifiedAd | undefined> {
    const data = this.mockDataService.createDb();
    const foundAd = data.classifiedAds.find((a: ClassifiedAd) => a.id === id);
    if (foundAd) {
      const updatedAd = {
        ...foundAd,
        ...ad,
        id,
        updatedDate: new Date(),
      };
      return of(updatedAd);
    }
    return of(undefined);
  }

  delete(id: number): Observable<boolean> {
    const data = this.mockDataService.createDb();
    const foundAd = data.classifiedAds.find((a: ClassifiedAd) => a.id === id);

    return of(!!foundAd);
  }

  getActive(): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    const activeAds = data.classifiedAds.filter((a: ClassifiedAd) => a.isActive);
    return of([...activeAds]);
  }

  getByCategory(category: string): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    const filteredAds = data.classifiedAds.filter((a: ClassifiedAd) => a.category === category);
    return of([...filteredAds]);
  }

  getByAuthor(authorId: number): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    const filteredAds = data.classifiedAds.filter((a: ClassifiedAd) => a.authorId === authorId);
    return of([...filteredAds]);
  }

  searchByContent(searchTerm: string): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    const filteredAds = data.classifiedAds.filter(
      (a: ClassifiedAd) =>
        a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return of([...filteredAds]);
  }

  getByPriceRange(minPrice: number, maxPrice: number): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    const filteredAds = data.classifiedAds.filter(
      (a: ClassifiedAd) => a.price && a.price >= minPrice && a.price <= maxPrice
    );
    return of([...filteredAds]);
  }

  getByLocation(location: string): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    const filteredAds = data.classifiedAds.filter(
      (a: ClassifiedAd) => a.location && a.location.toLowerCase().includes(location.toLowerCase())
    );
    return of([...filteredAds]);
  }

  getRecent(): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const recentAds = data.classifiedAds.filter(
      (a: ClassifiedAd) => a.createdDate >= sevenDaysAgo && a.isActive
    );
    return of([...recentAds]);
  }

  getLatestAds(count: number = 5): Observable<ClassifiedAd[]> {
    const data = this.mockDataService.createDb();
    const latestAds = data.classifiedAds
      .filter((ad: ClassifiedAd) => ad.isActive)
      .sort((a: ClassifiedAd, b: ClassifiedAd) => b.createdDate.getTime() - a.createdDate.getTime())
      .slice(0, count);
    return of([...latestAds]);
  }

  private getNextId(ads: ClassifiedAd[]): number {
    return Math.max(...ads.map((a: ClassifiedAd) => a.id)) + 1;
  }
}
